import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { SortDescriptor } from "@tql/ui-components-react/kendo/data-query";
import type {
	GridCustomCellProps,
	GridSelectionChangeEvent,
} from "@tql/ui-components-react/kendo/grid";
import type { TextBoxChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { InputPrefix, TextBox } from "@tql/ui-components-react/kendo/inputs";
import { searchSm, truck_01 } from "@tql/ui-components-react/untitled/icons";
import { useState } from "react";

import type {
	CarrierRow,
	Carriers,
} from "~/features/ar-dashboard/schemas/dashboard-schemas.ts";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";

import { ArDashboardCard } from "../ar-dashboard-card/ar-dashboard-card.tsx";
import styles from "./carriers-card.module.css";

type CarriersCardProps = {
	data: Carriers;
	isLoading?: boolean;
	isFetching?: boolean;
	selectedCarrierId?: number | null;
	onCarrierSelect?: (carrierId: number | null) => void;
	searchValue?: string;
	onSearchChange?: (value: string) => void;
};

type CarrierGridRow = CarrierRow & {
	id: number;
	ltlCountDisplay: string;
	avgHandlingUnitsDisplay: string;
	avgWeightDisplay: string;
	avgMilesDisplay: string;
	avgTransitDaysDisplay: string;
	selected?: boolean;
};

const numberFormatter = new Intl.NumberFormat("en-US", {
	maximumFractionDigits: 1,
});

const integerFormatter = new Intl.NumberFormat("en-US");

const DEFAULT_SORT: SortDescriptor[] = [{ field: "ltlCount", dir: "desc" }];

const toGridRow = (
	row: CarrierRow,
	selectedCarrierId: number | null | undefined,
): CarrierGridRow => ({
	...row,
	id: row.carrierId,
	ltlCountDisplay: integerFormatter.format(row.ltlCount),
	avgHandlingUnitsDisplay: `${numberFormatter.format(row.avgHandlingUnits)}`,
	avgWeightDisplay: `${integerFormatter.format(Math.round(row.avgWeight))} lbs`,
	avgMilesDisplay: `${integerFormatter.format(Math.round(row.avgMiles))} miles`,
	avgTransitDaysDisplay: `${numberFormatter.format(row.avgTransitDays)} days`,
	selected: selectedCarrierId === row.carrierId,
});

const carrierDetailFields: { label: string; key: keyof CarrierGridRow }[] = [
	{ label: "LTLs", key: "ltlCountDisplay" },
	{ label: "Avg Handling Units", key: "avgHandlingUnitsDisplay" },
	{ label: "Avg Weight", key: "avgWeightDisplay" },
	{ label: "Avg Miles", key: "avgMilesDisplay" },
	{ label: "Avg Transit Time", key: "avgTransitDaysDisplay" },
];

const CarrierDetail = ({ carrier }: { carrier: CarrierGridRow }) => {
	return (
		<div className={styles.detailPanel}>
			<div className={styles.detailHeader}>{carrier.carrierName}</div>
			{carrierDetailFields.map(({ label, key }) => (
				<div key={key} className={styles.detailRow}>
					<span className={styles.detailLabel}>{label}:</span>
					<span className={styles.detailValue}>{carrier[key]}</span>
				</div>
			))}
		</div>
	);
};

const CarriersCardContent = ({
	data,
	isLoading = false,
	isFetching = false,
	selectedCarrierId,
	onCarrierSelect,
}: CarriersCardProps) => {
	const showSkeleton = isLoading || isFetching;
	const [internalSelected, setInternalSelected] =
		useState<CarrierGridRow | null>(null);

	const rows = data.carriers.map((row) => toGridRow(row, selectedCarrierId));
	// Always resolve the detail carrier from the current rows so the panel
	// closes automatically when the underlying data clears (e.g. the user type
	// filter is cleared). Falling back to `internalSelected` directly would keep
	// the last carrier's details open against stale/empty data.
	const activeCarrierId =
		selectedCarrierId ?? internalSelected?.carrierId ?? null;
	const detailCarrier =
		rows.find((r) => r.carrierId === activeCarrierId) ?? null;

	const handleSelectionChange = (event: GridSelectionChangeEvent) => {
		// For row selection (no checkbox), Kendo exposes the clicked row via
		// `startDataItem`/`endDataItem`. `dataItem` is null and indexing
		// `dataItems` by `startRowIndex` breaks once the grid is sorted, which
		// previously caused every click to open the first carrier's details.
		const selectedItem = (event.startDataItem ?? event.endDataItem) as
			| CarrierGridRow
			| undefined;
		if (!selectedItem) {
			setInternalSelected(null);
			onCarrierSelect?.(null);
			return;
		}
		const nextId =
			selectedCarrierId === selectedItem.carrierId
				? null
				: selectedItem.carrierId;
		setInternalSelected(nextId === null ? null : selectedItem);
		onCarrierSelect?.(nextId);
	};

	const columns = [
		{
			field: "carrierName",
			title: "Most Frequent Carriers",
			editable: false,
		},
		{
			field: "ltlCount",
			title: " ",
			width: "80px",
			editable: false,
			cells: {
				data: (props: GridCustomCellProps) => (
					<GridTd tdProps={props.tdProps}>
						{(props.dataItem as CarrierGridRow).ltlCountDisplay}
					</GridTd>
				),
			},
		},
	];

	return (
		<div className={styles.container}>
			<div className={styles.gridSection}>
				<TraxGrid
					columns={columns}
					data={rows}
					dataItemKey={"id"}
					defaultSort={DEFAULT_SORT}
					defaultTake={Infinity}
					exactLoadingRows={5}
					loading={showSkeleton}
					maxHeight={"320px"}
					onSelectionChange={handleSelectionChange}
					pageable={false}
					scrollable={"scrollable"}
					selectable={{ enabled: true, mode: "single" }}
					sortable={true}
				/>
			</div>
			{!showSkeleton && detailCarrier ? (
				<div className={styles.detailSection}>
					<CarrierDetail carrier={detailCarrier} />
				</div>
			) : null}
		</div>
	);
};

export const CarriersCard = ({
	searchValue,
	onSearchChange,
	...contentProps
}: CarriersCardProps) => {
	return (
		<ArDashboardCard
			content={<CarriersCardContent {...contentProps} />}
			headerContent={
				<TextBox
					className={styles.searchInput}
					onChange={(e: TextBoxChangeEvent) =>
						onSearchChange?.(e.value as string)
					}
					placeholder={"Search carriers..."}
					prefix={() => (
						<InputPrefix>
							<SvgIcon icon={searchSm} />
						</InputPrefix>
					)}
					value={searchValue ?? ""}
				/>
			}
			icon={truck_01}
			title={"Carriers"}
			variant={"alt"}
		/>
	);
};
